import { createAutocomplete } from '@algolia/autocomplete-core';
import { getAlgoliaResults } from '@algolia/autocomplete-preset-algolia';
import PropTypes from 'prop-types';
import React from 'react';

import { IconNavigationCloseNormal, IconNavigationCloseOutlineFilled, IconNavigationSearchNormal } from '../../../components/Icons';
import { searchClient } from '../../../hooks/useAlgolia';
import useWindowSize from '../../../hooks/useWindowSize';
import * as css from './Autocomplete.module.scss';

const Autocomplete = ({ onReset, onSubmit, searchState }) => {
  // (1) Create a React state.
  const [autocompleteState, setAutocompleteState] = React.useState(searchState);
  const [showDetachedPanel, setShowDetachedPanel] = React.useState(false);
  const windowSize = useWindowSize();
  const autocomplete = React.useMemo(
    () =>
      createAutocomplete({
        onStateChange({ state }) {
          // (2) Synchronize the Autocomplete state with the React state.
          setAutocompleteState(state);
        },
        getSources() {
          return [
            // (3) Use an Algolia index source.
            {
              sourceId: 'tutorials',
              getItemInputValue({ item }) {
                return item.query;
              },
              getItems({ query }) {
                return getAlgoliaResults({
                  searchClient,
                  queries: [
                    {
                      indexName: 'globalsearch_query_suggestions',
                      query,
                      params: {
                        hitsPerPage: 4,
                        highlightPreTag: '<mark>',
                        highlightPostTag: '</mark>',
                      },
                    },
                  ],
                });
              },
              getItemUrl({ item }) {
                return item.url;
              },
            },
          ];
        },
        onSubmit: (params) => {
          setShowDetachedPanel(false);
          onSubmit(params);
        },
        onReset: (params) => {
          setShowDetachedPanel(false);
          onReset(params);
        },
        initialState: searchState,
        placeholder: 'Search tutorials',
      }),
    [onReset, onSubmit, searchState]
  );

  const inputRef = React.useRef(null);
  const formRef = React.useRef(null);
  const panelRef = React.useRef(null);
  const { getEnvironmentProps } = autocomplete;

  React.useEffect(() => {
    if (!formRef.current || !panelRef.current || !inputRef.current) {
      return undefined;
    }

    const { onTouchStart, onTouchMove } = getEnvironmentProps({
      formElement: formRef.current,
      inputElement: inputRef.current,
      panelElement: panelRef.current,
    });

    window.addEventListener('touchstart', onTouchStart);
    window.addEventListener('touchmove', onTouchMove);

    return () => {
      window.removeEventListener('touchstart', onTouchStart);
      window.removeEventListener('touchmove', onTouchMove);
    };
  }, [getEnvironmentProps, formRef, inputRef, panelRef]);

  const form = (
    <form
      ref={formRef}
      {...autocomplete.getFormProps({ inputElement: inputRef.current })}
      className={autocompleteState && autocompleteState.isOpen && css.open}
    >
      <div className={css.inputWrapperPrefix}>
        <label {...autocomplete.getLabelProps({})}>
          <button title="Search" type="submit">
            <IconNavigationSearchNormal />
          </button>
        </label>
      </div>
      <div className={css.inputWrapper}>
        <input
          ref={inputRef}
          {...autocomplete.getInputProps({})}
          onFocus={() => (windowSize.isMobile && !showDetachedPanel ? setShowDetachedPanel(true) : null)}
        />
      </div>
      <div className={css.inputWrapperSuffix}>
        <button title="Clear" type="reset" hidden={autocomplete.getInputProps({}).value === ''}>
          <IconNavigationCloseOutlineFilled />
        </button>
      </div>
    </form>
  );

  const panel = (
    <div className={css.panel} {...autocomplete.getPanelProps({})}>
      {autocompleteState && autocompleteState.isOpen && (
        <div ref={panelRef} className={css.panelLayout}>
          {autocompleteState.collections.map((collection, index) => {
            const { source, items } = collection;

            return (
              <div key={`source-${index}`} className={css.source}>
                {items.length > 0 && (
                  <ul {...autocomplete.getListProps()}>
                    {items.map((item) => {
                      return (
                        <li
                          key={item.objectID}
                          {...autocomplete.getItemProps({
                            item,
                            source,
                          })}
                          onClick={() => {
                            autocompleteState.isOpen = false;
                            setShowDetachedPanel(false);
                            onSubmit({ state: { ...autocompleteState, query: item.query } });
                          }}
                        >
                          {item.query}
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );

  return (
    <>
      <div className={css.autocompleteWrapper} {...autocomplete.getRootProps({})}>
        {form}
        {!windowSize.isMobile && panel}
      </div>
      {showDetachedPanel && (
        <div className={css.detachedOverlay}>
          <div className={css.detachedContainer}>
            <div className={css.detachedFormContainer}>
              {form}
              <button onClick={() => setShowDetachedPanel(false)}>
                <IconNavigationCloseNormal />
              </button>
            </div>
            {panel}
          </div>
        </div>
      )}
    </>
  );
};

Autocomplete.propTypes = {
  onSubmit: PropTypes.func,
  onReset: PropTypes.func,
  searchState: PropTypes.object,
};

export default Autocomplete;
